// ./src/components/generic/DateRangeFilter/DateRangeFilter.tsx
import React, { useState, useMemo } from "react";
import {
  Popover,
  Button,
  Stack,
  Group,
  Text,
  Divider,
  UnstyledButton,
  Box,
} from "@mantine/core";
import { DatePicker, DatesRangeValue } from "@mantine/dates";
import dayjs from "dayjs";
import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface DateRangeFilterProps {
  /** Current date range value */
  value: DateRange;
  /** Callback when date range changes */
  onChange: (range: DateRange) => void;
  /** Whether the filter is disabled */
  disabled?: boolean;
  /** Size of the control */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Placeholder text when no date is selected */
  placeholder?: string;
}

// ============================================
// Preset Definitions
// ============================================

interface DatePreset {
  label: string;
  getValue: () => DateRange;
}

const getDatePresets = (): DatePreset[] => {
  const today = dayjs().startOf("day");

  return [
    {
      label: "Today",
      getValue: () => ({
        from: today.toDate(),
        to: today.endOf("day").toDate(),
      }),
    },
    {
      label: "Yesterday",
      getValue: () => ({
        from: today.subtract(1, "day").toDate(),
        to: today.subtract(1, "day").endOf("day").toDate(),
      }),
    },
    {
      label: "Last 7 Days",
      getValue: () => ({
        from: today.subtract(6, "day").toDate(),
        to: today.endOf("day").toDate(),
      }),
    },
    {
      label: "Last 30 Days",
      getValue: () => ({
        from: today.subtract(29, "day").toDate(),
        to: today.endOf("day").toDate(),
      }),
    },
    {
      label: "This Week",
      getValue: () => ({
        from: today.startOf("week").toDate(),
        to: today.endOf("week").toDate(),
      }),
    },
    {
      label: "Last Week",
      getValue: () => ({
        from: today.subtract(1, "week").startOf("week").toDate(),
        to: today.subtract(1, "week").endOf("week").toDate(),
      }),
    },
    {
      label: "This Month",
      getValue: () => ({
        from: today.startOf("month").toDate(),
        to: today.endOf("month").toDate(),
      }),
    },
    {
      label: "Last Month",
      getValue: () => ({
        from: today.subtract(1, "month").startOf("month").toDate(),
        to: today.subtract(1, "month").endOf("month").toDate(),
      }),
    },
  ];
};

// ============================================
// Helper Functions
// ============================================

const formatDateRange = (range: DateRange): string => {
  if (!range.from && !range.to) {
    return "";
  }

  const fromStr = range.from ? dayjs(range.from).format("MMM D, YYYY") : "";
  const toStr = range.to ? dayjs(range.to).format("MMM D, YYYY") : "";

  if (fromStr === toStr) {
    return fromStr;
  }

  if (fromStr && toStr) {
    // Check if same year, use shorter format
    const fromYear = dayjs(range.from).year();
    const toYear = dayjs(range.to).year();

    if (fromYear === toYear) {
      return `${dayjs(range.from).format("MMM D")} - ${dayjs(range.to).format(
        "MMM D, YYYY"
      )}`;
    }

    return `${fromStr} - ${toStr}`;
  }

  return fromStr || toStr;
};

const findMatchingPreset = (range: DateRange): string | null => {
  if (!range.from || !range.to) return null;

  const presets = getDatePresets();
  const from = dayjs(range.from).startOf("day");
  const to = dayjs(range.to).startOf("day");

  for (const preset of presets) {
    const presetRange = preset.getValue();
    const presetFrom = dayjs(presetRange.from).startOf("day");
    const presetTo = dayjs(presetRange.to).startOf("day");

    if (from.isSame(presetFrom) && to.isSame(presetTo)) {
      return preset.label;
    }
  }

  return null;
};

/**
 * Convert Date to Mantine DatePicker format (Date object for value prop)
 */
const dateToPickerValue = (date: Date | null): Date | null => {
  return date;
};

/**
 * Parse Mantine DatePicker string value to Date
 */
const parsePickerValue = (value: Date | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

// ============================================
// Component
// ============================================

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  disabled = false,
  size = "sm",
  placeholder = "All Time",
}) => {
  const [opened, setOpened] = useState(false);
  const [tempRange, setTempRange] = useState<[Date | null, Date | null]>([
    value.from,
    value.to,
  ]);

  const presets = useMemo(() => getDatePresets(), []);

  // Display text for the button
  const displayText = useMemo(() => {
    const matchingPreset = findMatchingPreset(value);
    if (matchingPreset) return matchingPreset;

    const formatted = formatDateRange(value);
    return formatted || placeholder;
  }, [value, placeholder]);

  const hasValue = value.from !== null || value.to !== null;

  // Handle preset selection
  const handlePresetClick = (preset: DatePreset) => {
    const range = preset.getValue();
    onChange(range);
    setTempRange([range.from, range.to]);
    setOpened(false);
  };

  // Handle calendar date selection - Mantine v8 uses DatesRangeValue
  const handleDateChange = (dates: DatesRangeValue) => {
    const fromDate = parsePickerValue(dates[0]);
    const toDate = parsePickerValue(dates[1]);

    setTempRange([fromDate, toDate]);

    // If both dates are selected, apply the filter
    if (fromDate && toDate) {
      onChange({
        from: dayjs(fromDate).startOf("day").toDate(),
        to: dayjs(toDate).endOf("day").toDate(),
      });
      setOpened(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    onChange({ from: null, to: null });
    setTempRange([null, null]);
    setOpened(false);
  };

  // Sync temp range when popover opens
  const handlePopoverOpen = () => {
    setTempRange([value.from, value.to]);
    setOpened(true);
  };

  // Convert tempRange to picker value format
  const pickerValue: [Date | null, Date | null] = [
    dateToPickerValue(tempRange[0]),
    dateToPickerValue(tempRange[1]),
  ];

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <Button
          variant={hasValue ? "light" : "default"}
          color={hasValue ? "brand" : undefined}
          size={size}
          onClick={handlePopoverOpen}
          disabled={disabled}
          rightSection={
            hasValue ? (
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                style={{ display: "flex", cursor: "pointer" }}
              >
                <AppIcon iconName="X" size={14} />
              </Box>
            ) : (
              <AppIcon iconName="ChevronDown" size={14} />
            )
          }
          leftSection={<AppIcon iconName="Calendar" size={14} />}
          styles={{
            root: {
              fontWeight: 400,
            },
          }}
        >
          {displayText}
        </Button>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Group align="flex-start" gap={0} wrap="nowrap">
          {/* Presets column */}
          <Stack
            gap={0}
            p="xs"
            style={{
              borderRight: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Text size="xs" c="dimmed" fw={500} mb={4} px="xs">
              Quick Select
            </Text>
            {presets.map((preset) => {
              const presetRange = preset.getValue();
              const isActive =
                value.from &&
                value.to &&
                dayjs(value.from)
                  .startOf("day")
                  .isSame(dayjs(presetRange.from).startOf("day")) &&
                dayjs(value.to)
                  .startOf("day")
                  .isSame(dayjs(presetRange.to).startOf("day"));

              return (
                <UnstyledButton
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  px="xs"
                  py={6}
                  style={{
                    borderRadius: "var(--mantine-radius-sm)",
                    backgroundColor: isActive
                      ? "var(--mantine-color-brand-light)"
                      : undefined,
                  }}
                >
                  <Text
                    size="sm"
                    c={isActive ? "brand" : undefined}
                    fw={isActive ? 500 : 400}
                  >
                    {preset.label}
                  </Text>
                </UnstyledButton>
              );
            })}
            <Divider my="xs" />
            <UnstyledButton
              onClick={handleClear}
              px="xs"
              py={6}
              style={{
                borderRadius: "var(--mantine-radius-sm)",
              }}
            >
              <Text size="sm" c="dimmed">
                Clear / All Time
              </Text>
            </UnstyledButton>
          </Stack>

          {/* Calendar column */}
          <Stack gap="xs" p="xs">
            <Text size="xs" c="dimmed" fw={500} px="xs">
              Custom Range
            </Text>
            <DatePicker
              type="range"
              value={pickerValue}
              onChange={handleDateChange}
              numberOfColumns={2}
              maxDate={new Date()}
            />
            {tempRange[0] && !tempRange[1] && (
              <Text size="xs" c="dimmed" ta="center">
                Select end date
              </Text>
            )}
          </Stack>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};
